/* eslint-disable react/jsx-props-no-spreading */
import React from "react";
import { capitalize, debounce } from "lodash";
import * as moment from "moment";
import { connect } from "react-redux";

import Spinner from "web_modules/shared/Spinner";
import EmptyRow from "web_modules/shared/Table/EmptyRow";
import APIClient from "web_modules/APIClient";
import { prepareTableCell } from "web_modules/shared/Table/helpers";
import {
  ContactShape,
  TaskOwner,
  TaskShape,
  TaskStatus,
  TASK_STAGE_OPTIONS_BY_OWNER,
} from "@eave/shared/types/api";
import classnames from "classnames";
import {
  loadApps as reduxLoadApps,
  loadTasks as reduxLoadTasks,
  loadLMs as reduxLoadLMs,
} from "web_modules/Queues/redux";
import {
  ContentSwitcher,
  Switch,
  DataTable,
  TableContainer,
  TableHead,
  TableHeader,
  TableExpandHeader,
  TableRow,
  TableExpandRow,
  TableExpandedRow,
  TableCell,
  TableBody,
  Table,
} from "carbon-components-react";
import { getConfig, isProduction } from "config";

import QueueTable from "./QueueTable";
import AllUWQueuesData from "./UWQueuesData";
import { TaskingAppShape } from "./types";
import { nextTasksSorted, QueueRole } from "./shared";
import FilterByLM from "./FilterByLM";

const Pusher = require("pusher-js");

const styles = require("./styles.module.scss");

export const EMPTY_PLACEHOLDER: string = "-";

const selectedQueueRoleOptions = [
  {
    text: "Underwriters",
    name: QueueRole.Underwriter,
  },
  {
    text: "Loan Managers",
    name: QueueRole.LoanManager,
  },
];

interface Props {
  apps: TaskingAppShape[];
  tasks: TaskShape[];
  loan_managers: ContactShape[];
  sortKey: string;
  loadApps: Function;
  loadTasks: Function;
  loadLMs: Function;
}

interface State {
  selectedQueueRole: QueueRole | null;
  selectedLoanManagerName: string | null;
  loadingRows: number[];
  expandedRowData: object;
}

// Key in localStorage that stores the user's preferred queue filter (lm/uw)
const filterRoleViewKey = "TRUSSelectedQueueRole";
const filterLMTasksUserKey = "TRUSSelectedLoanManager";

const getLocalStorageKey = (key: string, options: any[]) => {
  const value = localStorage.getItem(key);

  if (value === null) {
    return null;
  }

  // If options for validation isn't passed, allow it to pass through
  if (options.length === 0) {
    return value;
  }

  // Search the list of valid options; if invalid, remove key altogether
  const selected = options.find(o => o.name === value);
  if (selected === undefined) {
    localStorage.removeItem(filterRoleViewKey);
    return null;
  }
  return selected.name;
};

class Queues extends React.Component<Props, State> {
  pusher = null;

  constructor(props) {
    super(props);
    this.state = {
      selectedQueueRole: null,
      selectedLoanManagerName: null,
      loadingRows: [],
      expandedRowData: {},
    };
    this.pusher = new Pusher(getConfig("pusher.key"), {
      encrypted: true,
      cluster: "mt1",
    });

    this.debouncedLoadTasks = debounce(this.debouncedLoadTasks, 2000);
  }

  setupPusher() {
    const prodPrefix = isProduction() ? "eave_" : "";
    const channelName = `${prodPrefix}tasks`;
    if (!this.pusher.channels.channels.hasOwnProperty(channelName)) {
      const channel = this.pusher.subscribe(channelName);

      channel.bind("tasks_updated", () => {
        this.debouncedLoadTasks();
      });
    }
  }

  debouncedLoadTasks = () => {
    const { loadTasks } = this.props;
    return loadTasks();
  };

  // Grab UI-related persisted values from localStorage
  initViewPreferences = () => {
    const prefs: any = {};

    // Remember the "Underwriter vs LM"
    prefs.roleView = getLocalStorageKey(filterRoleViewKey, selectedQueueRoleOptions);

    // Remember the LM to filter by
    prefs.filterByUser = getLocalStorageKey(filterLMTasksUserKey, []);

    return prefs;
  };

  componentDidMount() {
    const { loadApps, loadTasks, loadLMs } = this.props;

    loadApps();
    loadTasks();
    loadLMs(); // Load list of loan managers, for the filter

    this.setupPusher();

    const { roleView, filterByUser } = this.initViewPreferences();

    this.setState({
      selectedQueueRole: roleView,
      selectedLoanManagerName: filterByUser,
    });
  }

  loadTaskDetails(task) {
    const { id, loan_application_id, stage, category } = task;

    // If the data exists already, don't reload it
    if (this.state.expandedRowData[task.id] !== undefined) {
      return;
    }

    // Indicate loading status by adding it to currently-loading rows array
    this.setState(prevState => ({
      loadingRows: [...prevState.loadingRows, id],
    }));

    APIClient.get(`/uw/loan_applications/${loan_application_id}/tasks/${stage}/${category}`).then(
      (resp: any) => {
        // When request resolves,
        this.setState(prevState => {
          const currentTaskIndex = prevState.loadingRows.indexOf(id);
          const loadingRows = [...prevState.loadingRows];
          loadingRows.splice(currentTaskIndex, 1);

          return {
            loadingRows,
            expandedRowData: { ...prevState.expandedRowData, [id]: resp.tasks },
          };
        });
      },
    );
  }

  changeSelectedQueueRole(selectedOption) {
    this.setState({ selectedQueueRole: selectedOption.name });
    localStorage.setItem(filterRoleViewKey, selectedOption.name);
  }

  render() {
    const { apps, tasks, loan_managers } = this.props;
    const { selectedQueueRole, selectedLoanManagerName, loadingRows, expandedRowData } = this.state;

    let queuesData = AllUWQueuesData;

    // If selectedQueueRole is valid
    if (selectedQueueRole) {
      queuesData = AllUWQueuesData.filter(q => q.role === selectedQueueRole);
    }

    const showUWQueues = selectedQueueRole === null || selectedQueueRole === QueueRole.Underwriter;
    const showLMQueues = selectedQueueRole === QueueRole.LoanManager;

    const getHeaderWidth = key => {
      if (key === "loan_number") {
        return {
          maxWidth: "100px",
          whiteSpace: "pre-wrap",
        };
      }
      if (key === "category") {
        return {
          minWidth: "200px",
        };
      }
      if (key === "next_task") {
        return {
          minWidth: "200px",
        };
      }
      if (key === "closing_date") {
        return {
          maxWidth: "130px",
          padding: "3px",
        };
      }
      if (key === "task_due_date") {
        return {
          maxWidth: "140px",
          whiteSpace: "nowrap",
        };
      }

      return {};
    };

    const categoryCellSharedStyles: any = { position: "absolute", left: 0, top: 0, height: "100%" };

    return (
      <>
        <div className="underwriter-dashboard__children">
          <div
            className={classnames(
              "underwriter-dashboard__children-container",
              styles.queuesContainer,
            )}
          >
            <div className={styles.queuesControls}>
              <div className={styles.queuesRoleSelectWrapper}>
                <p className={styles.queuesRoleSelectLabel}>Show Queues For:</p>
                <div className={styles.queuesRoleSelect}>
                  <ContentSwitcher
                    onChange={action => {
                      this.changeSelectedQueueRole(action);
                    }}
                    selectedIndex={selectedQueueRoleOptions
                      .map(item => item.name)
                      .indexOf(this.state.selectedQueueRole)}
                  >
                    {selectedQueueRoleOptions.map(item => (
                      <Switch key={item.name} name={item.name} text={item.text} />
                    ))}
                  </ContentSwitcher>
                </div>
              </div>
            </div>

            <Spinner active={!apps} />

            {apps && loan_managers && (
              <>
                {showLMQueues && (
                  <FilterByLM
                    loanManagersList={loan_managers}
                    selectedLM={selectedLoanManagerName}
                    updateSelectedLM={v => this.setState({ selectedLoanManagerName: v })}
                  />
                )}

                {/* Queue Tables */}
                {showUWQueues &&
                  queuesData.map(q => <QueueTable key={q.name} rows={q.recipe(apps)} queue={q} />)}

                {showLMQueues &&
                  tasks &&
                  TASK_STAGE_OPTIONS_BY_OWNER[TaskOwner.LMLender].map(stage => {
                    const tasksInStage = tasks
                      .filter((t: TaskShape) => {
                        // If the key hasn't yet been set in localStorage, or if "All Loan Managers" is selected, show all
                        const allLoanManagersKey = "all";
                        if (
                          !selectedLoanManagerName ||
                          selectedLoanManagerName === allLoanManagersKey
                        ) {
                          return true;
                        }

                        // Otherwise, filter tasks by the loan manager
                        return !!(
                          t.loan_info &&
                          t.loan_info.loan_manager &&
                          t.loan_info.loan_manager.name === selectedLoanManagerName
                        );
                      })
                      .filter((t: TaskShape) => t.next_task.stage === stage);

                    const tasksInStageSorted = nextTasksSorted(tasksInStage);

                    return (
                      <div key={stage} style={{ marginBottom: "30px" }}>
                        {/* Filter applications by loan manager */}
                        <DataTable
                          sortRow={(cellA, cellB, { sortDirection, sortStates }) => {
                            const a = moment(cellA);
                            const b = moment(cellB);

                            if (a.isSame(b)) {
                              return 0;
                            }

                            // MOST RECENT DATES FIRST
                            if (sortDirection === sortStates.DESC) {
                              return b.isBefore(a) ? -1 : 1;
                            }

                            // OLDEST DATES FIRST
                            return a.isBefore(b) ? -1 : 1;
                          }}
                          rows={
                            tasksInStageSorted &&
                            tasksInStageSorted.map((t: TaskShape) => {
                              const { loan_info, next_task, next_task_category_completion_info } =
                                t;

                              const {
                                loan_application_id,
                                borrower1_first_name,
                                borrower1_last_name,
                                external_id,
                                lead_number,
                                closing_date,
                                loan_manager,
                              } = loan_info;

                              const { category, name, resolved_due_date, is_critical } = next_task;

                              return {
                                ...t,
                                id: `${t.next_task.id}`,
                                borrower: (
                                  <TableCell
                                    key={`${next_task.id}-borrower`}
                                    style={{
                                      position: "relative",
                                      padding: 0,
                                      paddingLeft: "1rem",
                                    }}
                                  >
                                    <a
                                      style={{ color: "#161616" }}
                                      href={`/underwriter/app/${loan_application_id}/TasksHurdle`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {capitalize(borrower1_first_name)}{" "}
                                      {capitalize(borrower1_last_name)}
                                    </a>
                                  </TableCell>
                                ),
                                loan_number: external_id || `(${lead_number})`,
                                closing_date: closing_date
                                  ? moment(closing_date).format("MM/DD/YYYY")
                                  : EMPTY_PLACEHOLDER,
                                category: (
                                  <TableCell
                                    key={`${next_task.id}-category`}
                                    style={{ position: "relative", padding: 0 }}
                                  >
                                    <div
                                      style={{
                                        ...categoryCellSharedStyles,
                                        background: "#eeeeee", // use the <th>'s background color
                                        width: "100%",
                                      }}
                                    />
                                    <div
                                      style={{
                                        ...categoryCellSharedStyles,
                                        background: "#e0e0e0", // use the <th>'s background color
                                        width: `${
                                          parseFloat(
                                            next_task_category_completion_info.percent_complete,
                                          ) * 100
                                        }%`,
                                        zIndex: 1,
                                      }}
                                    />
                                    <p
                                      style={{
                                        position: "absolute",
                                        top: "14px",
                                        left: "1rem",
                                        zIndex: 2,
                                      }}
                                    >
                                      {category}
                                    </p>
                                  </TableCell>
                                ),
                                next_task: name,
                                task_due_date: resolved_due_date
                                  ? moment(resolved_due_date).format("MM/DD/YYYY")
                                  : EMPTY_PLACEHOLDER,
                                is_critical: is_critical ? (
                                  <TableCell style={{ color: "red", fontWeight: "bold" }}>
                                    Yes
                                  </TableCell>
                                ) : (
                                  "No"
                                ),
                                days_until_due: resolved_due_date
                                  ? moment(resolved_due_date).startOf("day").fromNow()
                                  : EMPTY_PLACEHOLDER,
                                days_until_closing_date: closing_date
                                  ? moment(closing_date).startOf("day").fromNow()
                                  : EMPTY_PLACEHOLDER,
                                loan_manager: (loan_manager && loan_manager.name) || "-",
                              };
                            })
                          }
                          headers={[
                            /*
                             * WARNING: IF ADDING `isSortable` to a non-date field (e.g. "Category")
                             *   be sure to rework this <DataTable>'s `sortRow` prop, which currently only sorts dates
                             */
                            { header: "Borrower", key: "borrower" }, // see warning above
                            { header: "Loan # (Lead #)", key: "loan_number" }, // see warning above
                            { header: "COE Date", key: "closing_date", isSortable: true },
                            { header: "Days Until Closing", key: "days_until_closing_date" }, // see warning above
                            { header: "Category", key: "category" }, // see warning above
                            { header: "Next Task", key: "next_task" }, // see warning above
                            { header: "Task Due Date", key: "task_due_date", isSortable: true },
                            { header: "Critical?", key: "is_critical" },
                            { header: "Days Until Due", key: "days_until_due" }, // see warning above
                            { header: "LM", key: "loan_manager" }, // see warning above
                          ]}
                          render={({
                            rows,
                            headers,
                            getHeaderProps,
                            getRowProps,
                            getTableProps,
                          }) => {
                            return (
                              <TableContainer title={stage}>
                                <Table {...getTableProps()}>
                                  <TableHead>
                                    <TableRow style={{ whiteSpace: "nowrap" }}>
                                      <TableExpandHeader />
                                      {headers.map(header => (
                                        <TableHeader
                                          style={getHeaderWidth(header.key)}
                                          key={header.key}
                                          {...getHeaderProps({ header })}
                                          isSortable={header.isSortable} // NOTE: isSortable prop is an override, so place after `getHeaderProps()`
                                        >
                                          {header.header}
                                        </TableHeader>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {rows.length === 0 && (
                                      <EmptyRow
                                        width={11}
                                        subtext={
                                          selectedLoanManagerName !== "" &&
                                          selectedLoanManagerName !== null &&
                                          `Note: You are currently filtering by user ${selectedLoanManagerName}.`
                                        }
                                      />
                                    )}
                                    {tasksInStageSorted.length > 0 &&
                                      rows.map(row => {
                                        const currentLoanTaskData = tasksInStageSorted.find(
                                          t => `${t?.next_task?.id}` === row.id,
                                        )?.next_task;

                                        return (
                                          <React.Fragment key={`${stage}-${row.id}-row`}>
                                            <TableExpandRow
                                              {...getRowProps({
                                                row,
                                                onClick: () =>
                                                  this.loadTaskDetails(currentLoanTaskData),
                                              })}
                                            >
                                              {row.cells.map(cell => prepareTableCell(cell))}
                                            </TableExpandRow>
                                            {row.isExpanded && (
                                              <TableExpandedRow colSpan={headers.length + 1}>
                                                {loadingRows.includes(currentLoanTaskData.id) && (
                                                  <p>Loading</p>
                                                )}
                                                {!loadingRows.includes(currentLoanTaskData.id) &&
                                                  expandedRowData[currentLoanTaskData.id].length >
                                                    0 && (
                                                    <>
                                                      <DataTable
                                                        headers={[
                                                          {
                                                            header: "Task Status",
                                                            key: "status",
                                                          },
                                                          {
                                                            header: "Task Name",
                                                            key: "name",
                                                          },
                                                        ]}
                                                        rows={expandedRowData[
                                                          currentLoanTaskData.id
                                                        ].map(taskInCategory => {
                                                          return {
                                                            id: String(taskInCategory.id),
                                                            status: taskInCategory.status,
                                                            name: taskInCategory.name,
                                                          };
                                                        })}
                                                        render={({
                                                          rows,
                                                          headers,
                                                          getHeaderProps,
                                                          getRowProps,
                                                          getTableProps,
                                                        }) => (
                                                          <div style={{ maxWidth: "50%" }}>
                                                            <TableContainer
                                                              style={{
                                                                border: "1px solid #e0e0e0",
                                                                marginLeft: "25px",
                                                              }}
                                                              title={`${currentLoanTaskData.category}: Remaining Tasks`}
                                                            >
                                                              <Table {...getTableProps()}>
                                                                <TableHead>
                                                                  <TableRow>
                                                                    {headers.map(header => (
                                                                      <TableHeader
                                                                        style={
                                                                          header.key === "status"
                                                                            ? {
                                                                                backgroundColor:
                                                                                  "#e0e0e0",
                                                                                width: "140px",
                                                                              }
                                                                            : {
                                                                                backgroundColor:
                                                                                  "#e0e0e0",
                                                                              }
                                                                        }
                                                                        key={header.key}
                                                                      >
                                                                        {header.header}
                                                                      </TableHeader>
                                                                    ))}
                                                                  </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                  {rows.map(r => (
                                                                    <TableRow
                                                                      key={row.id}
                                                                      size="short"
                                                                      style={{
                                                                        opacity:
                                                                          r.cells[0].value ===
                                                                            TaskStatus.Cleared ||
                                                                          r.cells[0].value ===
                                                                            TaskStatus.NotApplicable
                                                                            ? 0.25
                                                                            : 1,
                                                                      }}
                                                                    >
                                                                      {r.cells.map(cell => (
                                                                        <TableCell
                                                                          key={cell.id}
                                                                          style={{
                                                                            backgroundColor:
                                                                              "#f4f4f4",
                                                                          }}
                                                                        >
                                                                          {cell.value}
                                                                        </TableCell>
                                                                      ))}
                                                                    </TableRow>
                                                                  ))}
                                                                </TableBody>
                                                              </Table>
                                                            </TableContainer>
                                                          </div>
                                                        )}
                                                      />
                                                    </>
                                                  )}
                                              </TableExpandedRow>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            );
                          }}
                        />
                      </div>
                    );
                  })}

                {selectedQueueRole !== null && (
                  <div style={{ paddingTop: "1rem" }} className={styles.showAllQueuesPrompt}>
                    <span>You're currently viewing queues for {selectedQueueRole} ONLY. </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = state => {
  return {
    apps: state.queues.apps,
    tasks: state.queues.tasks,
    loan_managers: state.queues.loan_managers,
  };
};

const mapDispatchToProps = dispatch => ({
  loadApps: () => reduxLoadApps(dispatch),
  loadTasks: () => reduxLoadTasks(dispatch),
  loadLMs: () => reduxLoadLMs(dispatch),
});

export default connect<any, any>(mapStateToProps, mapDispatchToProps)(Queues);
